import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Tooltip from 'rc-tooltip';
import Spinner from '../self-booking/0-atoms/Spinner';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    route?: string;
    disabledReason?: string;
    loading?: boolean;
    href?: string;
    tooltip?: string;
    tooltipPlacement?: string;
    tooltipProps?: any;
    size?: 'compact' | 'small' | 'large';
    variant?:
        | 'primary'
        | 'secondary'
        | 'neutral'
        | 'accent'
        | 'accent-secondary'
        | 'text-only'
        | 'destructive-primary'
        | 'destructive-secondary'
        | 'unstyled';
}

const buttonSizes = {
    compact: {
        'min-width': 'auto',
        height: '28px',
        padding: '0 8px',
        'border-radius': '6px',
        'font-size': '12px',
    },
    small: {
        'min-width': '71px',
        height: '40px',
        padding: '16px 16px',
        'border-radius': '6px',
        'font-size': '14px',
    },
    large: {
        'min-width': '77px',
        height: '56px',
        padding: '16px 16px',
        'border-radius': '8px',
        'font-size': '16px',
    },
};

const buttonVariants = {
    primary: {
        backgroundColor: '#303F9F',
        color: '#FFFFFF',
        border: 'none',
    },
    secondary: {
        backgroundColor: '#FFFFFF',
        color: '#303F9F',
        border: '2px solid #303F9F',
    },
    neutral: {
        backgroundColor: '#FFFFFF',
        color: '#212121',
        border: '2px solid #CCCCCC',
    },
    accent: {
        backgroundColor: '#340065',
        color: '#FFFFFF',
        border: 'none',
    },
    'accent-secondary': {
        backgroundColor: '#FFFFFF',
        color: '#340065',
        border: '2px solid #340065',
    },
    'text-only': {
        backgroundColor: '#FFFFFF',
        color: '#212121',
        border: 'none',
    },
    'destructive-primary': {
        backgroundColor: '#D32F2F',
        color: '#FFFFFF',
        border: 'none',
    },
    'destructive-secondary': {
        backgroundColor: '#FFFFFF',
        color: '#EF4647',
        border: '2px solid #D32F2F',
    },
    unstyled: {
        backgroundColor: 'transparent',
        color: 'inherit',
        border: 'none',
    },
};

const ButtonComp = styled.button<{ $size?: string; $variant?: string }>`
    font-family: 'Montserrat', tahoma !important;
    min-width: ${(props) => buttonSizes[props.$size || 'small']['min-width']};
    height: ${(props) => buttonSizes[props.$size || 'small'].height};
    padding: ${(props) => buttonSizes[props.$size || 'small'].padding};
    background-color: ${(props) =>
        buttonVariants[props.$variant || 'primary'].backgroundColor};
    color: ${(props) => buttonVariants[props.$variant || 'primary'].color};
    border: ${(props) => buttonVariants[props.$variant || 'primary'].border};
    border-radius: ${(props) =>
        buttonSizes[props.$size || 'small']['border-radius']};
    font-size: ${(props) => buttonSizes[props.$size || 'small']['font-size']};
    font-weight: 500;
    line-height: 19px;
    transition:
        opacity 0.2s,
        background-color 0.2s,
        filter 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    :visited {
        background-color: inherit !important;
    }
    :disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    :hover:not(:disabled) {
        ${(props) => {
            if (props.$variant === 'unstyled') return '';
            if (props.$variant === 'text-only') {
                return `background-color: rgba(0, 0, 0, 0.04);`;
            }
            return `
               filter: brightness(0.96);
            `;
        }}
    }
`;

const renderWithTooltip = (element: React.ReactElement, props: ButtonProps) => {
    const placement = props.tooltipPlacement || 'bottom';
    const hasTooltip =
        props.tooltip || (props.disabled && props.disabledReason);
    const tooltipText = props.tooltip || props.disabledReason;

    if (!hasTooltip) return element;

    return (
        <Tooltip
            placement={placement}
            trigger={['hover']}
            overlay={<span>{tooltipText}</span>}
            {...props.tooltipProps}
        >
            <div>{element}</div>
        </Tooltip>
    );
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (props, ref) => {
        const {
            disabled,
            loading,
            variant,
            size,
            className,
            route,
            ...restProps
        } = props;

        const getAsProperty = () => {
            if (route) return Link;
            if (props.href && !(disabled || loading)) return 'a'; // Add the disabled check
            return 'button';
        };

        return renderWithTooltip(
            <ButtonComp
                as={getAsProperty()}
                ref={ref}
                $variant={variant}
                $size={size}
                disabled={disabled || loading}
                className={className}
                to={route || undefined}
                {...restProps}
            >
                {loading ? <Spinner variant={variant} /> : props.children}
            </ButtonComp>,
            props,
        );
    },
);

export { Button };
